import {ChangeDetectorRef, Component, NgZone, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {map, Observable, pairwise, startWith, take} from 'rxjs';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {PreviewPageTab} from '../../enum/preview-page-tabs';
import { Route } from 'src/app/enum/route';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../store/proposal/reducers/proposal.reducer';
import {
  selectDetailPageProposal,
  selectProposalPreview,
  selectProposalTilePreview
} from '../../store/proposal/selectors/proposal.selectors';
import {Proposal} from '../../model/proposal/proposal';
import {DialogTwoOptionsComponent} from '../../components/dialogs/two-options-dialog/dialog-two-options.component';
import {DialogConfirmComponent} from '../../components/dialogs/dialog-confirm/dialog-confirm.component';
import {LocalStorageItem} from '../../enum/local-storage-item';
import {FilterRole} from '../../enum/filter-role';
import {TranslateService} from '@ngx-translate/core';
import {SpinnerDialogComponent} from '../../components/dialogs/spinner-dialog/spinner-dialog.component';
import {ProposalService} from '../../services/proposal-service/proposal.service';
import {UploadComponent} from '../../components/upload-field/upload.component';
import {ProposalDetailPageActions} from '../../store/proposal/actions/proposal.actions';
import {animate, style, transition, trigger} from '@angular/animations';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {FileUploadService} from '../../services/file-upload-service/file-upload.service';

@Component({
    selector: 'app-proposal-preview-page',
    templateUrl: './proposal-preview-page.component.html',
    styleUrls: ['./proposal-preview-page.component.scss', '../proposal-detail/proposal-details.component.scss', '../../../styles/create-something.scss'],
    animations: [
        trigger('swipeAnimation', [
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('400ms ease-in-out', style({ transform: 'translateX(0%)' })),
            ]),
            transition(':leave', [
                animate('400ms ease-in-out', style({ transform: 'translateX(100%)' })),
            ]),
        ])
    ],
    standalone: false
})
export class ProposalPreviewPageComponent implements OnInit {

  allDetailsOpen: boolean = false;
  proposal = this.proposalStore.select(selectProposalPreview);

  proposalTile = this.proposalStore.select(selectProposalTilePreview);

  selectedTab$: Observable<PreviewPageTab[]>;
  Tab = PreviewPageTab;

  Header = Header;
  Route = Route;
  currentHeader: Header = Header.none;
  @ViewChild('tabBar') tabBar;
  @ViewChild('title') title;
  @ViewChild('header') header;

  showComponent: boolean = true;

  isEditRoute: boolean = false;
  editedProposalId: string | null = null;

  @ViewChildren(UploadComponent) uploadComponents: QueryList<UploadComponent>;

  constructor(readonly activatedRoute: ActivatedRoute,
              public cd: ChangeDetectorRef,
              private readonly translate: TranslateService,
              readonly dialog: MatDialog,
              readonly router: Router,
              public ngZone: NgZone,
              public proposalStore: Store<ProposalAppState>,
              private readonly proposalService: ProposalService,
              private readonly fileUploadService: FileUploadService) {}

  ngOnInit(): void {
    this.selectedTab$ = this.activatedRoute.queryParamMap.pipe(map(queryMap => {
      const tab = queryMap.get('tab');
      return Object.values<string>(PreviewPageTab).includes(tab) ? tab as PreviewPageTab : PreviewPageTab.DETAILS;
    }), startWith(null), pairwise());


    this.editedProposalId = this.activatedRoute.snapshot.paramMap.get('id');
    this.isEditRoute = this.editedProposalId != null;
  }

  onWindowScroll() {
    const tabBarScrolled = this.tabBar?.nativeElement.getBoundingClientRect().top;
    const titleScrolled = this.title?.nativeElement.getBoundingClientRect().top;
    const headerHeight = this.header?.nativeElement.getBoundingClientRect().height ?? 0;
    let oldHeader = this.currentHeader;
    if (0 < titleScrolled) {
      this.currentHeader = Header.none;
    }
    if (titleScrolled < 0) {
      this.currentHeader = Header.title;
    }

    if (tabBarScrolled < headerHeight) {
      this.currentHeader = Header.tabs;
    }
    if (oldHeader != this.currentHeader) {
      this.cd.detectChanges();
    }
  }

  changeTab(tab: PreviewPageTab) {
    const queryParams: Params = {tab: tab};
    if (tab == PreviewPageTab.DETAILS) {
      this.allDetailsOpen = false;
    }
    this.ngZone.run(() =>
      this.router.navigate(
        [],
        {
          relativeTo: this.activatedRoute,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        })?.then())?.then();
  }

  cancel() {
    this.router.navigate(['/' + Route.INSERATE]).then();
  }

  openConfirmationMatDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: this.translate.instant('createProposal.confirmation.title'),
      optionCancel: this.translate.instant('createProposal.confirmation.no'),
      optionConfirm: this.translate.instant('createProposal.confirmation.yes'),
    };
    this.ngZone.run(() => {
      const dialogRef = this.dialog.open(DialogTwoOptionsComponent, dialogConfig);
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          localStorage.setItem(LocalStorageItem.SELECTED_ROLE, FilterRole.ALL);
          this.openSpinnerDialog();
        }
      });
    });
  }

  openSpinnerDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: '',
    };
    const dialogRef = this.dialog.open(SpinnerDialogComponent, dialogConfig);
    if(this.isEditRoute){
      this.editProposal(dialogRef);
    }else {
      this.saveProposal(dialogRef);
    }
  }



  saveProposal(dialogRef: MatDialogRef<SpinnerDialogComponent>) {
    let checkedProposal = new Proposal();

    this.proposal.pipe(take(1)).subscribe(prop => checkedProposal = prop);

    this.attachedFilesCheck(checkedProposal)

    this.proposalService.saveProposal(checkedProposal).subscribe({
      next: () => {
        this.router.navigate(['/' + Route.INSERATE]).then(() => {
          dialogRef.close();
          // Reset preview only if saveProposal is successful
          this.proposalStore.dispatch(ProposalDetailPageActions.resetPreview());
        });
      },
      error: () => {
        dialogRef.close();
      }
    });

  }

  editProposal(dialogRef: MatDialogRef<SpinnerDialogComponent>) {
    this.proposal.pipe(take(1)).subscribe(previewProp => {
      this.proposalStore.select(selectDetailPageProposal).pipe(take(1)).subscribe(originalProp => {
        const originalProposal: Proposal = originalProp;
                const editedProposal: Proposal = new Proposal(previewProp);
        editedProposal.proposalInformation.id = originalProposal.proposalInformation.id;

        this.attachedFilesCheck(editedProposal);

        this.proposalService.editProposal(editedProposal).subscribe({
          next: () => {
            this.router.navigate(['/' + Route.INSERATE]).then(() => {
              dialogRef.close();
              this.proposalStore.dispatch(ProposalDetailPageActions.resetPreview());
              if (originalProposal.proposalInformation.emailsReceivedPDFWithDate?.length > 0) {
                const noticeConfig = new MatDialogConfig();
                noticeConfig.disableClose = false;
                noticeConfig.autoFocus = true;
                noticeConfig.data = {
                  title: this.translate.instant('editProposal.recipientsNotice.title'),
                  body: this.translate.instant('editProposal.recipientsNotice.body'),
                };
                this.ngZone.run(() => this.dialog.open(DialogConfirmComponent, noticeConfig));
              }
            });
          },
          error: () => {
            dialogRef.close();
          }
        });
      });
    });
  }

  navigateToCreateProposal() {
    this.showComponent = !this.showComponent; //Trigger animation
    setTimeout(() => {
      this.ngZone.run(() => {
        this.router.navigate(['/' + Route.INSERAT_ERSTELLEN],
            {queryParams: {previousRoute: 'previewPage'}})?.then();
      });
    }, 150);

  }


  attachedFilesCheck(checkedProposal: Proposal){
    // if there are some files in the original proposal that aren't anymore in the new edited proposal, that means that those files got deleted
    // this for loop and endpoint call ensures that the file is deleted on backend as well and not just on the UI
    if (checkedProposal.proposalInformation?.id != null) {
      this.proposalService.getProposal(checkedProposal.proposalInformation.id).subscribe(originalProposal => {
        originalProposal.proposalInformation.attachedFiles.forEach(file => {
          if (!checkedProposal.proposalInformation.attachedFiles.some(f => f.id === file.id)) {
            this.fileUploadService.deleteFileData(file.id);
          }
        })
      })
    }
  }
}



export default ProposalPreviewPageComponent;

export enum Header {
  'none',
  'title',
  'tabs'
}
